import { DefaultError, QueryClient, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { corporateCMSApi } from "~/Services";
import { PageHeading } from "../MediaCentre/Media.hook";

interface ManagementTeam {
  id: number;
  title: string;
  field_title_name_ms: string;
  field_title_name_zh: string;
  field_designation_en: string;
  field_designation_ms: string;
  field_designation_zh: string;
  field_description_en: string;
  field_description_ms: string;
  field_description_zh: string;
  field_link_en: string;
  field_link_ms: string;
  field_link_zh: string;
  field_page_en: string;
  field_page_ms: string;
  field_page_zh: string;
  field_organisation_image: string;
  field_categories: string;
  field_categories_ms: string;
  field_categories_zh: string;
  field_organisation_order_sequenc: number;
  published: boolean;
  field_organisation_created_at: string;
  field_organisation_updated_at: string;
}

interface CompanyStructure {
  id: number;
  title: string;
  field_content_english: {
    data: {
      image: string;
      text: string;
      nextPage: {
        name: string;
        link: string;
      };
    };
    labelData: any;
  };
  field_content_malay: {
    data: {
      image: string;
      text: string;
      nextPage: {
        name: string;
        link: string;
      };
    };
    labelData: any;
  };
  field_content_mandarin: {
    data: {
      image: string;
      text: string;
      nextPage: {
        name: string;
        link: string;
      };
    };
    labelData: any;
  };
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}
interface ContentData {
  nextPage: {
    name: string;
    link: string;
  };
}
interface NextPage {
  id: number;
  field_content_english: {
    data: ContentData;
  };
  field_content_malay: {
    data: ContentData;
  };
  field_content_mandarin: {
    data: ContentData;
  };
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

interface manageNextPage {
  id: number;
  field_content_english: {
    data: ContentData;
  };
  field_content_malay: {
    data: ContentData;
  };
  field_content_mandarin: {
    data: ContentData;
  };
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}
// edit api call
const fetchManagementTeamDetails = async (id: string): Promise<any> => {
  if (!id) {
    return null;
  }
  const response: any = await corporateCMSApi.get(`/api/get-organisation-id/${id}`);
  return response.data[0];
};

const useManagementTeamDetails = (
  id: string,
  options?: UseQueryOptions<ManagementTeam, DefaultError, ManagementTeam, QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<ManagementTeam, DefaultError> => {
  return useQuery<ManagementTeam, DefaultError, ManagementTeam, QueryKey>(
    {
      queryKey: ["managementTeam", id],
      enabled: false,
      queryFn: () => {
        return fetchManagementTeamDetails(id);
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useBoardOfDirectorsDetails = (
  id: string,
  options?: UseQueryOptions<ManagementTeam, DefaultError, ManagementTeam, QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<ManagementTeam, DefaultError> => {
  return useQuery<ManagementTeam, DefaultError, ManagementTeam, QueryKey>(
    {
      queryKey: ["boardOfDirectors", id],
      enabled: false,
      queryFn: () => {
        return fetchManagementTeamDetails(id);
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useCompanyStructureDetails = (
  options?: UseQueryOptions<CompanyStructure[], DefaultError, CompanyStructure[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<CompanyStructure[], DefaultError> => {
  return useQuery<CompanyStructure[], DefaultError, CompanyStructure[], QueryKey>(
    {
      queryKey: ["companyStructure"],
      enabled: false,
      queryFn: async (): Promise<CompanyStructure[]> => {
        const response = await corporateCMSApi.get<CompanyStructure[]>(
          `/api/page-content/all-fields?pageName=about-us/organisation/company-structure`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useManagementTeamDetailsPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["managementTeamDetailsPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=about-us/organisation/management-team`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useBoardOfDirectorsDetailsPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["boardOfDirectorsDetailsPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=about-us/organisation/board-directors`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useCompanyStructureDetailsPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["companyStructureDetailsPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=about-us/organisation/company-structure`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useNextPage = (
  options?: UseQueryOptions<NextPage[], DefaultError, NextPage[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<NextPage[], DefaultError> => {
  return useQuery<NextPage[], DefaultError, NextPage[], QueryKey>(
    {
      queryKey: ["ourProfileNextPage"],
      enabled: false,
      queryFn: async (): Promise<NextPage[]> => {
        const response = await corporateCMSApi.get<NextPage[]>(
          `/api/page-content/all-fields?pageName=about-us/organisation/board-directors`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useManagementNextPage = (
  options?: UseQueryOptions<manageNextPage[], DefaultError, manageNextPage[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<manageNextPage[], DefaultError> => {
  return useQuery<manageNextPage[], DefaultError, manageNextPage[], QueryKey>(
    {
      queryKey: ["managementNextPage"],
      enabled: false,
      queryFn: async (): Promise<manageNextPage[]> => {
        const response = await corporateCMSApi.get<manageNextPage[]>(
          `/api/page-content/all-fields?pageName=about-us/organisation/management-team`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

export {
  useBoardOfDirectorsDetails,
  useBoardOfDirectorsDetailsPageHeading,
  useCompanyStructureDetails,
  useCompanyStructureDetailsPageHeading,
  useManagementNextPage,
  useManagementTeamDetails,
  useManagementTeamDetailsPageHeading,
  useNextPage,
};
